/**
 * Iter XX.198 — Meta / Acquisition funnel event wrapper (frontend).
 *
 * Fire-and-forget POST to `/api/acquisition/track` for the two funnel
 * milestones the server cannot infer on its own:
 *
 *   · plus_paywall_viewed  — Paywall / Plus surface impression
 *   · purchase_cta_tapped  — purchase CTA tapped (NOT checkout started)
 *     (`purchase_started` remains as a deprecated dual-emit alias
 *     until 2026-10-09 — see CLIENT_TRACKABLE below.)
 *
 * The server enriches the event with the request IP + UA, then mirrors
 * it into Meta CAPI (dedup by event_id against the frontend Pixel).
 *
 * Never blocks the caller. Always resolves (never throws).
 */
import api from "./api";
import { getPlatform } from "./nativePlatform";
import { getBuildInfo } from "./nativeAppInfo";

const CLIENT_TRACKABLE = new Set([
  "plus_paywall_viewed",
  // Iter XX.285f — TERMINOLOGY FIX (2026-10-02, founder-mandated):
  // `purchase_started` historically meant "purchase CTA tapped" but was
  // misread as "checkout started". It is now DEPRECATED — dual-emitted
  // alongside `purchase_cta_tapped` until 2026-10-09, then removed.
  // New canonical chain:
  //   purchase_cta_tapped → checkout_request_started →
  //   checkout_session_created → checkout_opened →
  //   payment_completed / checkout_cancelled / checkout_failed
  // `purchase_cta_tapped` is a CTA metric, NOT a checkout attempt.
  // `checkout_request_started` is the checkout-attempt metric.
  // None of the new chain events mirror to Meta CAPI (first-party only).
  "purchase_started", // DEPRECATED — dual-emit alias of purchase_cta_tapped until 2026-10-09
  "purchase_cta_tapped",
  "checkout_request_started",
  "checkout_session_created",
  "payment_completed",
  // Iter XX.282 — Canonical CTA tap + user-cancelled hint. Additive.
  // Both are first-party only; the server does NOT mirror them to
  // Meta CAPI (see /api/acquisition/track wiring).
  "plus_cta_tapped",
  "checkout_cancelled",
  // Iter XX.283 — Native `checkout_failed` (RevenueCat / Play / StoreKit).
  // Same event name as the Stripe web `checkout_failed` (server-emitted
  // from plus_billing) so a single dashboard splits by channel +
  // normalized `error_category`. First-party only, no CAPI mirror.
  "checkout_failed",
]);

/**
 * Read a browser cookie by name. Returns null if not present.
 * @param {string} name
 */
function _readCookie(name) {
  try {
    if (typeof document === "undefined") return null;
    // Simple linear scan is safer than regex-with-escaping given the
    // small cookie set we're targeting.
    const parts = document.cookie ? document.cookie.split(";") : [];
    for (let i = 0; i < parts.length; i++) {
      const kv = parts[i].trim();
      if (kv.slice(0, name.length + 1) === name + "=") {
        return decodeURIComponent(kv.slice(name.length + 1));
      }
    }
    return null;
  } catch { return null; }
}

/** Meta `_fbp` browser id — set by the Pixel loader. */
export function getFbp()  { return _readCookie("_fbp"); }
/** Meta `_fbc` click id — set by the Pixel when a user lands via `fbclid`. */
export function getFbc()  { return _readCookie("_fbc"); }

/**
 * Fire one funnel event to the backend. `event_name` must be one of the
 * client-trackable milestones — the backend rejects everything else.
 *
 * @param {"plus_paywall_viewed"|"purchase_started"} event_name
 * @param {object} [opts]
 * @param {string} [opts.session_id]    web session id (falls back to localStorage)
 * @param {string} [opts.browser_id]    persistent browser id
 * @param {string} [opts.milestone_key] optional dedup key (e.g. sku, price_id)
 * @param {object} [opts.extra]         opaque payload passed through
 * @returns {Promise<{ok: boolean, event_id?: string, reason?: string}>}
 */
export async function trackMetaFunnelEvent(event_name, opts = {}) {
  if (!CLIENT_TRACKABLE.has(event_name)) {
    return { ok: false, reason: "event_not_client_trackable" };
  }
  const session_id = opts.session_id
    || (typeof localStorage !== "undefined"
          ? localStorage.getItem("gully_web_session_id")
          : null);
  const browser_id = opts.browser_id
    || (typeof localStorage !== "undefined"
          ? (localStorage.getItem("gully_browser_id")
             || localStorage.getItem("g_browser_id"))
          : null);
  // Iter XX.295 — Binary identity on every funnel event (commercial P0).
  // Native builds without RevenueCat keys were incapable of completing
  // purchases; every downstream event must name the exact binary so zero
  // purchases are never misread as price rejection. Explicit caller
  // values win — we only fill gaps.
  const extra = { ...(opts.extra || {}) };
  try {
    const build = await getBuildInfo();
    if (extra.build_id == null && build.build_id) {
      extra.build_id = build.build_id;
    }
    if (extra.app_version == null && build.app_version) {
      extra.app_version = build.app_version;
    }
    if (extra.app_build == null && build.app_build) {
      extra.app_build = build.app_build;
    }
  } catch {
    /* never block the funnel on telemetry */
  }
  const body = {
    event_name,
    session_id,
    browser_id,
    client_platform: getPlatform(),
    milestone_key: opts.milestone_key || "",
    fbp: getFbp(),
    fbc: getFbc(),
    extra,
  };
  try {
    const { data } = await api.post("/acquisition/track", body);
    return data || { ok: false };
  } catch (e) {
    // Never bubble — funnel is fire-and-forget.
    return { ok: false, reason: "network_error" };
  }
}

export const CLIENT_TRACKABLE_EVENTS = Array.from(CLIENT_TRACKABLE);
